import { cn } from '@/lib/utils';

import { type SankeyNodeWithLinks } from './types';
import { getWords } from './util';

type Props = {
  node: SankeyNodeWithLinks;
  maxLevel: number;
  textFilterId: string;
  isEmphasized: boolean;
  labelHorizontalPadding?: number;
};

const SimpleSankeyNodeLabel = ({
  node,
  maxLevel,
  textFilterId,
  isEmphasized,
  labelHorizontalPadding = 12,
}: Props) => {
  const isNodeOriginating = !node.sourceLink;
  const isNodeTerminating = !node.targetLinks?.length;
  const showValue = isNodeOriginating || isNodeTerminating;
  const canHideLabel = node.level > 1 && node.level < maxLevel;
  const canBreakWords = canHideLabel;

  const getTextX = (node: SankeyNodeWithLinks, maxLevel: number) => {
    if (!node.sourceLink) {
      return node.x - labelHorizontalPadding;
    } else if (node.level > 1 && node.level < maxLevel) {
      return node.x + labelHorizontalPadding;
    }
    return node.x + node.width + labelHorizontalPadding;
  };

  const { words, labelHeight, valueShown } = getWords(
    node.name,
    node.value,
    node.height,
    showValue,
    canHideLabel,
    canBreakWords,
  );
  const textX = getTextX(node, maxLevel);
  const textY = node.y + node.height / 2;

  return (
    <text
      className={cn(
        'pointer-events-none fill-gray-900 font-sans text-xs font-semibold transition-opacity duration-500 ease-in-out select-none [dominant-baseline:middle]',
      )}
      x={textX}
      y={node.y + node.height / 2}
      textAnchor={isNodeOriginating ? 'end' : 'start'}
      filter={`url(#${textFilterId})`}
      opacity={isEmphasized ? 1 : 0.25}
    >
      {words.map((word, i) => (
        <tspan
          key={i}
          x={textX}
          y={textY - labelHeight / 2 + i * 14 + 14 / 2}
          dy={1}
          fontSize={valueShown && i === words.length - 1 ? 12 : undefined}
          fontWeight={valueShown && i === words.length - 1 ? 400 : undefined}
          opacity={valueShown && i === words.length - 1 ? 0.8 : undefined}
          pointerEvents="all"
        >
          {word}
        </tspan>
      ))}
    </text>
  );
};

export default SimpleSankeyNodeLabel;
